/**
 * Component for BackstageTrack
 *
 * @module component/BackstageTrack
 */

import { List } from 'immutable';
import noop from 'lodash/noop';
import PropTypes from 'prop-types';
import React from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Link } from 'react-router';

import { annotateObjectsForBackstage, getCatalogDetailsForBackstage } from '../actions';
import { BackstageLoggerDefaultArgs, messages } from '../constants';
import * as D from '../deps';
import { getBackstageAnnotation, getCommonBackstageProps } from '../utils';

import BackstageTrackList from './BackstageTrackList';

const { AUDIO_PLAY_TRACK, PAUSE } = D.Constants.AudioPlayer.AudioPlayerActions;
const { ADDTOCOLLECTION, REMOVEFROMCOLLECTION, SHARE } = D.Constants.SourceCard.SourceCardConstants;
const { STATION_TRIGGER } = D.Constants.SourceCard.SourceCardTriggerTypes;
const LyricsWithLogger = D.withVisibilityLogger({
  contentSection: D.Constants.ContentSections.BackstageContentSections.LYRICS,
})(D.Lyrics);
const BackstageTrackListWithLogger = D.withVisibilityLogger(
  {
    contentSection: D.Constants.ContentSections.BackstageContentSections.TRACK_LIST,
  },
  { staticHeight: D.RowItemHeights.DEFAULT, useMemo: true },
)(BackstageTrackList);

const songCreditsMesssage = <FormattedMessage {...messages.songCredits} />;
const seeAllSongCreditsMesssage = <FormattedMessage {...messages.seeAllSongCredits} />;
const lyricsMesssage = <FormattedMessage {...messages.lyrics} />;
const seeAllLyricsMesssage = <FormattedMessage {...messages.seeAllLyrics} />;

export class BackstageTrack extends React.Component {
  static propTypes = {
    pandoraId: PropTypes.string,
    encryptedPandoraId: PropTypes.string,
    getTrack: PropTypes.func.isRequired,
    getCatalogDetailsForBackstage: PropTypes.func.isRequired,
    songTitle: PropTypes.string,
    artistName: PropTypes.string,
    albumArt: PropTypes.string,
    focusTraits: ImmutablePropTypes.listOf(PropTypes.string),
    similarTracks: ImmutablePropTypes.listOf(PropTypes.string),
    tracksCopyright: PropTypes.string,
    openShareModal: PropTypes.func.isRequired,
    lyricSnippet: ImmutablePropTypes.listOf(PropTypes.string),
    credits: ImmutablePropTypes.map,
    albumId: PropTypes.string,
    albumTitle: PropTypes.string,
    artistUrl: PropTypes.string,
    albumUrl: PropTypes.string,
    openDropdown: PropTypes.func.isRequired,
    closeDropdown: PropTypes.func.isRequired,
    shouldRenderBSPSidebarButtons: PropTypes.bool,
    isMobileWeb: PropTypes.bool,
    disableHover: PropTypes.bool,
    // T1/T2 specific
    // T3 specific
    onDemandIsEnabled: PropTypes.bool,
    inCollection: PropTypes.bool,
    addCollectionItem: PropTypes.func.isRequired,
    removeCollectionItem: PropTypes.func.isRequired,
    ellipsisSourceCardMenuItems: PropTypes.array,
    stationSourceCardMenuItems: PropTypes.array,
    pauseTrack: PropTypes.func,
    isPlayingCurrently: PropTypes.bool,
    isPaused: PropTypes.bool,
    isUnavailable: PropTypes.bool,
    isRadioOnly: PropTypes.bool,
    isExplicit: PropTypes.bool,
    isClean: PropTypes.bool,
    hasRadio: PropTypes.bool,
    shareableUrlPath: PropTypes.string,
    isRightRailActive: PropTypes.bool,
    annotateObjectsForBackstage: PropTypes.func.isRequired,
    children: PropTypes.element,
    playResumeSource: PropTypes.func,
    showPremiumAccess: PropTypes.bool,
  };

  componentDidMount() {
    const { pandoraId } = this.props;
    if (pandoraId) {
      this.props.getCatalogDetailsForBackstage(pandoraId, true);
      this.props.getTrack(D.getMusicIdFromPandoraId(pandoraId), false, false);
    }

    if (this.props.similarTracks && this.props.similarTracks.size > 0) {
      this.props.annotateObjectsForBackstage(this.props.similarTracks.toJS(), true);
    }
  }

  shouldComponentUpdate(nextProps) {
    return D.arePropsDifferent(nextProps, this.props);
  }

  componentDidUpdate(prevProps) {
    // Prevent a redundant call using the unencrypted pandoraId, if we already made data calls using the encryptedPandoraId
    const isPandoraIdUpdated = prevProps.pandoraId !== this.props.encryptedPandoraId;
    if (
      this.props.pandoraId &&
      this.props.pandoraId !== prevProps.pandoraId &&
      isPandoraIdUpdated
    ) {
      this.props.getCatalogDetailsForBackstage(this.props.pandoraId, true);
      this.props.getTrack(D.getMusicIdFromPandoraId(this.props.pandoraId), false, false);
    }

    if (
      this.props.similarTracks &&
      this.props.similarTracks.size > 0 &&
      !this.props.similarTracks.equals(prevProps.similarTracks)
    ) {
      this.props.annotateObjectsForBackstage(this.props.similarTracks.toJS(), true);
    }
  }

  static contextType = D.AppContext;

  getIntegrationDataFactory = name => {
    return () => {
      const contexts = {
        controlBar: {
          ...BackstageLoggerDefaultArgs.controlBar,
          ...{ pandoraId: this.props.pandoraId },
        },
      };
      return contexts[name] || BackstageLoggerDefaultArgs[name];
    };
  };

  getBadgeList = () => {
    const badgeList = [];
    if (this.props.isExplicit) {
      badgeList.push(D.BadgeTypes.EXPLICIT_SHORT);
    }

    if (this.props.isClean) {
      badgeList.push(D.BadgeTypes.CLEAN);
    }

    if (this.props.isRadioOnly) {
      badgeList.push(D.BadgeTypes.RADIO);
    }
    return List(badgeList);
  };

  addToMusicClick = () => {
    const { pandoraId } = this.props;
    this.componentEventFuncHandler({
      ...BackstageLoggerDefaultArgs.controlBar,
      ...{ pandoraId, componentEventType: ADDTOCOLLECTION },
    });
    this.props.addCollectionItem({ pandoraId });
  };

  removeMusicClick = () => {
    this.componentEventFuncHandler({
      ...BackstageLoggerDefaultArgs.controlBar,
      ...{ componentEventType: REMOVEFROMCOLLECTION },
    });
    this.props.removeCollectionItem({ pandoraId: this.props.pandoraId });
  };

  playOnClick = () => {
    const { pandoraId, showPremiumAccess } = this.props;
    this.componentEventFuncHandler({
      ...BackstageLoggerDefaultArgs.controlBar,
      ...{ pandoraId, componentEventType: AUDIO_PLAY_TRACK },
    });
    this.props.playResumeSource({
      id: pandoraId,
      pandoraType: D.Constants.SourceTypes.PandoraTypes.SONG,
      forceRestartSourceIfPlaying: showPremiumAccess,
      context: D.Constants.Ads.Rewards.ContextTypes.BACKSTAGE,
      shouldShuffle: false,
      pageUrl: this.props.shareableUrlPath,
    });
  };

  pauseOnClick = () => {
    this.componentEventFuncHandler({
      ...BackstageLoggerDefaultArgs.controlBar,
      ...{ componentEventType: PAUSE },
    });
    this.props.pauseTrack();
  };

  onShareClick = () => {
    this.componentEventFuncHandler({
      ...BackstageLoggerDefaultArgs.controlBar,
      ...{ componentEventType: SHARE },
    });
    this.props.openShareModal(this.props.pandoraId, {
      shareParentSourceId: this.props.albumId,
      shareParentSourceType: D.PandoraTypes.ALBUM,
    });
  };

  onStationIconClick = () => {
    this.componentEventFuncHandler({
      ...BackstageLoggerDefaultArgs.controlBar,
      ...{ componentEventType: STATION_TRIGGER },
    });
  };

  onHeaderLinkClick = e => {
    this.componentEventFuncHandler({
      ...BackstageLoggerDefaultArgs.header,
      ...{ destinationUrl: e.currentTarget.pathname },
    });
  };

  onSeeAllLyricsClick = e => {
    this.componentEventFuncHandler({
      ...BackstageLoggerDefaultArgs.lyrics,
      ...{ destinationUrl: e.currentTarget.pathname },
    });
  };

  onSeeAllCreditsClick = e => {
    this.componentEventFuncHandler({
      ...BackstageLoggerDefaultArgs.credits,
      ...{ destinationUrl: e.currentTarget.pathname },
    });
  };

  onSeeAllSimilarSongsClick = e => {
    this.componentEventFuncHandler({
      ...BackstageLoggerDefaultArgs.similarSongs,
      ...{ destinationUrl: e.currentTarget.pathname },
    });
  };

  componentEventFuncHandler = ({
    pandoraId,
    contentSection,
    componentEventType,
    destinationUrl,
    currentIndex,
  }) => {
    this.context.integration.action.clicked({
      pandoraId,
      componentEventType,
      contentSection,
      destinationUrl,
      currentIndex,
    });
  };

  renderSideBarButtons = () => {
    let variableProps = {
      hiddenButtons: [D.ButtonRowType.MORE],
      playOnClick: this.playOnClick,
    };

    if (this.props.onDemandIsEnabled) {
      const { inCollection, isPaused, isPlayingCurrently } = this.props;
      variableProps = {
        addToMusicOnClick: !inCollection ? this.addToMusicClick : noop,
        removeFromMusicOnClick: inCollection ? this.removeMusicClick : noop,
        playOnClick: !isPlayingCurrently || isPaused ? this.playOnClick : noop,
        pauseOnClick: isPlayingCurrently && !isPaused ? this.pauseOnClick : noop,
        ellipsisSourceCardMenuItems: this.props.ellipsisSourceCardMenuItems,
      };
    }

    const disabledButtons =
      this.props.isRadioOnly || this.props.isUnavailable
        ? [D.ButtonRowType.ADD, D.ButtonRowType.PLAY]
        : [];

    const buttonRowProps = {
      pandoraId: this.props.pandoraId,
      stationSourceCardMenuItems: this.props.stationSourceCardMenuItems,
      shareOnClick: this.onShareClick,
      stationIconOnClick: this.onStationIconClick,
      disabledButtons,
      isSidebar: true,
      buttonTextType: D.ButtonTextType.PLAY_SONG,
    };

    return (
      <D.AppContextIntegrationData data={this.getIntegrationDataFactory('controlBar')}>
        <D.ButtonRowWithLogger {...buttonRowProps} {...variableProps} />
      </D.AppContextIntegrationData>
    );
  };

  renderSimilarSongs = () => {
    const similarTracks = this.props.similarTracks;
    const NUM_SONGS_TO_RENDER = this.props.isMobileWeb ? 3 : 5;
    const moreUrl = D.getBackstageTrackSimilarSongsPathForShareUrl(this.props.shareableUrlPath);
    if (!similarTracks || similarTracks.isEmpty()) return;
    return (
      <D.AppContextIntegrationData data={this.getIntegrationDataFactory('similarSongs')}>
        <D.ContentSection
          isSimilarSongs={true}
          moreTextOnclick={this.onSeeAllSimilarSongsClick}
          title={<FormattedMessage {...messages.similarSongs} />}
          moreText={<FormattedMessage {...messages.seeAllSimilarSongs} />}
          moreUrl={moreUrl}
          disableHover={this.props.disableHover}
        >
          <BackstageTrackListWithLogger
            tracks={similarTracks.take(NUM_SONGS_TO_RENDER)}
            numPlaceholders={NUM_SONGS_TO_RENDER}
            elementHeight={D.RowItemHeights.DEFAULT}
            showArtistName={true}
          />
        </D.ContentSection>
      </D.AppContextIntegrationData>
    );
  };

  renderLyrics = () => {
    if (!this.props.lyricSnippet) {
      return null;
    }

    const moreUrl = D.getBackstageTrackLyricsPathForShareUrl(this.props.shareableUrlPath);
    return (
      <D.ContentSection
        isSnippet={true}
        title={lyricsMesssage}
        moreText={seeAllLyricsMesssage}
        moreTextOnclick={this.onSeeAllLyricsClick}
        moreUrl={moreUrl}
        data-qa={D.QA.LSA}
        disableHover={this.props.disableHover}
      >
        <LyricsWithLogger
          onComponentInView={this.onComponentInView}
          onComponentInViewArgs={BackstageLoggerDefaultArgs.lyrics}
          isBackstage={true}
          pandoraId={this.props.pandoraId}
          key={`${this.props.pandoraId}_lyrics`}
          songTitle={this.props.songTitle}
          artistName={this.props.artistName}
          lyricSnippet={this.props.lyricSnippet}
        />
      </D.ContentSection>
    );
  };

  renderCredits = () => {
    if (!this.props.credits) {
      return null;
    }

    const moreUrl = D.getBackstageTrackCreditsPathForShareUrl(this.props.shareableUrlPath);
    return (
      <D.ContentSection
        isSnippet={true}
        title={songCreditsMesssage}
        moreText={seeAllSongCreditsMesssage}
        moreTextOnclick={this.onSeeAllCreditsClick}
        moreUrl={moreUrl}
        disableHover={this.props.disableHover}
      >
        <D.DescriptionBackstage
          text={this.props.credits.get('creditsSnippet')}
          isExpanded={true}
          isSmall={true}
          pandoraId={this.props.pandoraId}
          disableHover={this.props.disableHover}
        />
      </D.ContentSection>
    );
  };

  renderStructuredMetaData = () => {
    const { pandoraId } = this.props.pandoraId;
    if (!pandoraId) {
      return;
    }
    return (
      <D.StructuredMetadata
        isTrack={true}
        songTitle={this.props.songTitle}
        albumTitle={this.props.albumTitle}
        artistName={this.props.artistName}
        pandoraId={this.props.pandoraId}
        image={this.props.albumArt}
      />
    );
  };

  renderMainContent = () => {
    if (this.props.children) {
      return <div>{React.cloneElement(this.props.children, this.props)}</div>;
    }
    const lyricsSnippet = this.renderLyrics();
    const creditsSnippet = this.renderCredits();
    return (
      <React.Fragment>
        <div className='BackstageLayout__cols'>
          {(lyricsSnippet || creditsSnippet) && (
            <div className='BackstageLayout__cols__col'>
              {lyricsSnippet}
              {creditsSnippet}
            </div>
          )}
          {this.props.focusTraits && !this.props.focusTraits.isEmpty() && (
            <div className='BackstageLayout__cols__col'>
              <D.ContentSection
                isFocusTraits={true}
                title={<FormattedMessage {...messages.focusTraits} />}
              >
                <D.FocusTraitsWithLogger focusTraits={this.props.focusTraits} />
              </D.ContentSection>
            </div>
          )}
        </div>
        {this.props.similarTracks &&
          !this.props.similarTracks.isEmpty() &&
          this.renderSimilarSongs()}
        {this.props.tracksCopyright && (
          <D.CopyrightWithLogger copyrights={this.props.tracksCopyright} />
        )}
      </React.Fragment>
    );
  };

  render() {
    const showBadge = this.props.isRadioOnly || this.props.isExplicit || this.props.isClean;
    const headerProps = {
      title: this.props.songTitle || '',
      titleUrl: this.props.shareableUrlPath,
      badges: showBadge ? this.getBadgeList() : null,
      disableHover: this.props.disableHover,
      isBackstage: true,
      subTitleLrg:
        (
          <Link
            to={this.props.artistUrl}
            onClick={this.onHeaderLinkClick}
            className={D.HeaderBackstageStyles.SUBTITLE_LINK}
            data-qa={D.QA.BTAL}
          >
            {this.props.artistName}
          </Link>
        ) || null,
      subTitleSm: (
        <span>
          <FormattedMessage {...messages.fromTheAlbum} />
          <Link
            to={this.props.albumUrl}
            onClick={this.onHeaderLinkClick}
            className={D.HeaderBackstageStyles.SUBTITLE_LINK}
            data-qa={D.QA.BTLL}
          >
            {this.props.albumTitle}
          </Link>
        </span>
      ),
    };
    return (
      <D.BackstageLayout
        headerProps={this.props.songTitle && headerProps}
        heroImageUrl={this.props.albumArt}
        heroImageLinkUrl={this.props.shareableUrlPath}
        renderSidebarButtons={
          this.props.shouldRenderBSPSidebarButtons ? this.renderSideBarButtons : null
        }
        renderArtworkPlayButton={this.props.isMobileWeb}
        renderMainContent={this.renderMainContent}
        entityIdentifier={this.props.pandoraId}
        entityGetterFunc={this.props.getCatalogDetailsForBackstage}
        playOnClick={this.playOnClick}
        {...this.props}
      />
    );
  }
}

export function mapStateToProps(state, ownProps) {
  const encryptedPandoraId = ownProps && ownProps.params && ownProps.params.pandoraId;
  const annotation = getBackstageAnnotation(state, encryptedPandoraId);
  if (
    !annotation ||
    annotation.isEmpty() ||
    ![D.PandoraTypes.SONG].includes(annotation.get('type'))
  ) {
    return {
      pandoraId: encryptedPandoraId ? encryptedPandoraId : null,
    };
  }
  const pandoraId = annotation.get('pandoraId');
  const catalogDetails = D.getInDomain('catalog.utils.getCatalogDetailsFromState')(
    state,
    pandoraId,
  );
  const props = getCommonBackstageProps(state, encryptedPandoraId, pandoraId);

  if (catalogDetails && !catalogDetails.isEmpty() && annotation) {
    props.isPaused = !D.getInDomain('playback.utils.isAudioPlaying', false)(state);
    props.isPlayingCurrently = D.getInDomain('playback.utils.isTrackPlayingCurrently', false)(
      state,
      pandoraId,
    );
    props.similarTracks = D.getFromMap(catalogDetails, 'similarTracks');
    props.focusTraits = D.getFromMap(catalogDetails, 'trackTags');
    props.tracksCopyright = D.getFromMap(catalogDetails, 'copyright');
    props.isExplicit = D.getInDomain('catalog.utils.isExplicit')(annotation);
    props.isClean = D.getInDomain('catalog.utils.isClean')(annotation);
    props.songTitle = D.getFromMap(annotation, 'name');
    props.isRadioOnly = D.isRadioOnly(annotation);
    props.shareableUrlPath = annotation.get('shareableUrlPath');
    props.hasRadio = D.getFromMap(annotation, 'hasRadio');
    props.isUnavailable = D.isUnavailable(annotation);
    props.albumArt = D.getArt(annotation, 1080, state);
    props.credits = D.getFromMap(catalogDetails, 'credits');

    const artistId = D.getFromMap(annotation, 'artistId');
    const artistLink = D.getInDomain('catalog.utils.getCatalogEntityLink')(state, artistId);
    props.artistName = artistLink.name;
    props.artistUrl = artistLink.shareableUrlPath;

    const albumId = D.getFromMap(annotation, 'albumId');
    const albumLink = D.getInDomain('catalog.utils.getCatalogEntityLink')(state, albumId);
    props.albumId = albumId;
    props.albumTitle = albumLink.name;
    props.albumUrl = albumLink.shareableUrlPath;

    props.inCollection = D.getInDomain('collection.utils.isPandoraIdInCollection', false)(
      state,
      props.pandoraId,
      albumId,
    );
    props.ellipsisSourceCardMenuItems = props.onDemandIsEnabled
      ? D.getSourceCardMenuItems({
          state,
          pandoraId: props.pandoraId,
          pandoraType: D.PandoraTypes.SONG,
          shareParentSource: {
            shareParentSourceId: albumId,
            shareParentSourceType: D.PandoraTypes.ALBUM,
          },
          isBackstagePage: true,
          albumId,
        })
      : null;
    props.stationSourceCardMenuItems = D.getSourceCardMenuItems({
      state,
      pandoraId: props.pandoraId,
      pandoraType: D.PandoraTypes.SONG,
      shareParentSource: {
        shareParentSourceId: albumId,
        shareParentSourceType: D.PandoraTypes.ALBUM,
      },
      isBackstagePage: true,
      isStationButton: true,
      albumId,
    });
    props.lyricSnippet = D.getInDomain('catalog.utils.getLyricSnippet')(state, pandoraId);
  }

  props.showPremiumAccess = D.Utils.Listener.showPremiumAccess(state);
  props.shouldRenderBSPSidebarButtons = D.shouldRenderBSPSidebarButtons(state);

  props.isMobileWeb = D.isMobileWeb(state);
  props.disableHover = D.disableHoverStates(state);

  return props;
}

const mapDispatchToProps = {
  getTrack: D.getTrack,
  openDropdown: D.openDropdown,
  closeDropdown: D.closeDropdown,
  addCollectionItem: D.addCollectionItem,
  removeCollectionItem: D.removeCollectionItem,
  pauseTrack: D.Actions.UseCases.pauseTrack,
  getCatalogDetailsForBackstage,
  annotateObjectsForBackstage,
  openShareModal: D.openShareModal,
  playResumeSource: D.playResumeSource,
};

export default connect(mapStateToProps, mapDispatchToProps)(BackstageTrack);
